import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePldAlertDetail, useReviewPldAlert } from '../../../hooks/usePld';
import { ArrowLeft, AlertTriangle, CheckCircle, XCircle, Clock, User, Building, CreditCard, MapPin } from 'lucide-react';

const severityColors = {
  LOW: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  HIGH: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  CRITICAL: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const statusColors = {
  PENDING: 'bg-yellow-500/20 text-yellow-400',
  UNDER_REVIEW: 'bg-blue-500/20 text-blue-400',
  CONFIRMED: 'bg-red-500/20 text-red-400',
  FALSE_POSITIVE: 'bg-green-500/20 text-green-400',
  ESCALATED: 'bg-purple-500/20 text-purple-400',
};

const statusLabels = {
  PENDING: 'Pendente',
  UNDER_REVIEW: 'Em Análise',
  CONFIRMED: 'Confirmado',
  FALSE_POSITIVE: 'Falso Positivo',
  ESCALATED: 'Escalado',
};

const resolutionOptions = [
  { value: 'FALSE_POSITIVE', label: 'Falso Positivo', description: 'A transação é legítima' },
  { value: 'CONFIRMED', label: 'Confirmado', description: 'Atividade suspeita confirmada' },
  { value: 'ESCALATED', label: 'Escalar', description: 'Encaminhar para análise superior' },
];

export default function PldAlertDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: alert, isLoading, error } = usePldAlertDetail(id!);
  const reviewMutation = useReviewPldAlert();
  
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);

  const handleReview = async () => {
    if (!resolution) return;
    
    try {
      await reviewMutation.mutateAsync({
        id: id!,
        resolution: resolution as 'FALSE_POSITIVE' | 'CONFIRMED' | 'ESCALATED',
        notes,
      });
      navigate('/admin/compliance/pld/alerts');
    } catch (err) {
      console.error('Erro ao revisar alerta:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="h-64 bg-surface-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Erro ao carregar alerta
        </div>
      </div>
    );
  }

  const canReview = ['PENDING', 'UNDER_REVIEW'].includes(alert.status);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/compliance/pld/alerts')}
            className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Alerta PLD #{alert.id.slice(0, 8)}</h1>
            <p className="text-surface-400">Detalhes do alerta de prevenção à lavagem de dinheiro</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${severityColors[alert.severity as keyof typeof severityColors]}`}>
            {alert.severity}
          </span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[alert.status as keyof typeof statusColors]}`}>
            {statusLabels[alert.status as keyof typeof statusLabels]}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Alert Info */}
          <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Informações do Alerta
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-surface-400">Tipo de Regra</label>
                <p className="font-medium">{alert.ruleType}</p>
              </div>
              
              <div>
                <label className="text-sm text-surface-400">Descrição</label>
                <p className="text-surface-300">{alert.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-surface-400">Data de Criação</label>
                  <p className="font-medium">
                    {new Date(alert.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Última Atualização</label>
                  <p className="font-medium">
                    {new Date(alert.updatedAt).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Info */}
          {alert.transaction && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary-400" />
                Transação Relacionada
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-surface-400">ID da Transação</label>
                  <p className="font-mono text-sm">{alert.transaction.id}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Tipo</label>
                  <p className="font-medium">{alert.transaction.type}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Valor</label>
                  <p className="font-medium text-lg">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(alert.transaction.amount)}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Data</label>
                  <p className="font-medium">
                    {new Date(alert.transaction.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Account Info */}
          {alert.account && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" />
                Conta Envolvida
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-surface-400">Titular</label>
                  <p className="font-medium">{alert.account.holderName}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Documento</label>
                  <p className="font-mono text-sm">{alert.account.document}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Agência/Conta</label>
                  <p className="font-medium">{alert.account.branch} / {alert.account.number}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Tipo</label>
                  <p className="font-medium">{alert.account.type}</p>
                </div>
              </div>
            </div>
          )}

          {/* Review Form */}
          {canReview && showReviewForm && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h2 className="text-lg font-semibold mb-4">Análise do Alerta</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Resolução</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {resolutionOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setResolution(option.value)}
                        className={`p-4 rounded-lg border text-left transition-colors ${
                          resolution === option.value
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-600 hover:border-surface-500'
                        }`}
                      >
                        <p className="font-medium">{option.label}</p>
                        <p className="text-sm text-surface-400">{option.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Observações</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Descreva sua análise e justificativa..."
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleReview}
                    disabled={!resolution || reviewMutation.isPending}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reviewMutation.isPending ? 'Salvando...' : 'Confirmar Análise'}
                  </button>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    className="px-6 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {canReview && !showReviewForm && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-4">Ações</h3>
              <button
                onClick={() => setShowReviewForm(true)}
                className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
              >
                Analisar Alerta
              </button>
            </div>
          )}

          {/* Rule Info */}
          {alert.rule && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-4">Regra Acionada</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-surface-400">Nome</label>
                  <p className="font-medium">{alert.rule.name}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Tipo</label>
                  <p className="font-medium">{alert.rule.type}</p>
                </div>
                <div>
                  <label className="text-sm text-surface-400">Severidade</label>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${severityColors[alert.rule.severity as keyof typeof severityColors]}`}>
                    {alert.rule.severity}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {alert.history && alert.history.length > 0 && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-4">Histórico</h3>
              <div className="space-y-4">
                {alert.history.map((event: any, index: number) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary-500"></div>
                    <div>
                      <p className="text-sm font-medium">{event.action}</p>
                      <p className="text-xs text-surface-400">
                        {new Date(event.createdAt).toLocaleString('pt-BR')}
                      </p>
                      {event.notes && (
                        <p className="text-sm text-surface-300 mt-1">{event.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviewer Info */}
          {alert.reviewedBy && (
            <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
              <h3 className="font-semibold mb-4">Revisado Por</h3>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-surface-600 flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">{alert.reviewedByName || alert.reviewedBy}</p>
                  <p className="text-sm text-surface-400">
                    {alert.reviewedAt && new Date(alert.reviewedAt).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
              {alert.reviewNotes && (
                <p className="mt-3 text-sm text-surface-300 bg-surface-700 p-3 rounded-lg">
                  {alert.reviewNotes}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
